import { AccountType as AnalyticsAccountType } from '@stashinvest/product-events-typescript';
import { ACCOUNT_TYPES, type AccountType } from 'stash/types/models/Accounts';

type ProductEventsAccountType =
	(typeof AnalyticsAccountType)[keyof typeof AnalyticsAccountType];

export const PERSONAL_BROKERAGE = ACCOUNT_TYPES.PERSONAL_BROKERAGE;
/** Close-account flow pseudo-type; not a real account model type. */
export const BROKERAGE_ACCOUNTS_COMBINED = 'BROKERAGE_ACCOUNTS_COMBINED' as const;
export const ROBO_PERSONAL_BROKERAGE = ACCOUNT_TYPES.ROBO_PERSONAL_BROKERAGE;
export const TRADITIONAL_IRA = ACCOUNT_TYPES.TRADITIONAL_IRA;
export const ROTH_IRA = ACCOUNT_TYPES.ROTH_IRA;
export const CUSTODIAN = ACCOUNT_TYPES.CUSTODIAN;
export const DEBIT = ACCOUNT_TYPES.DEBIT;

//Used for analytics events
export const ANALYTICS_ACCOUNT_TYPE: Partial<Record<AccountType, ProductEventsAccountType>> = {
	[PERSONAL_BROKERAGE]: AnalyticsAccountType.personalBrokerage,
	[ROBO_PERSONAL_BROKERAGE]: AnalyticsAccountType.personalBrokerage,
	[TRADITIONAL_IRA]: AnalyticsAccountType.traditionalIra,
	[ROTH_IRA]: AnalyticsAccountType.rothIra,
	[CUSTODIAN]: AnalyticsAccountType.custodial,
	[DEBIT]: AnalyticsAccountType.bank,
};

//Returned by /apis/experiences/v1/overviews/accounts/by/users/:uuid
export const PX_ACCOUNT_TYPES = {
	BANK_DDA: 'ACCOUNT_TYPE_BANK_DDA',
	CUSTODIAN: 'ACCOUNT_TYPE_CUSTODIAN',
	PERSONAL_BROKERAGE: 'ACCOUNT_TYPE_PERSONAL_BROKERAGE',
	ROBO_PERSONAL_BROKERAGE: 'ACCOUNT_TYPE_ROBO_PERSONAL_BROKERAGE',
	ROTH_IRA: 'ACCOUNT_TYPE_ROTH_IRA',
	STASH_BANK: 'ACCOUNT_TYPE_STASH_BANK',
	TRADITIONAL_IRA: 'ACCOUNT_TYPE_TRADITIONAL_IRA',
} as const;

export type PxAccountType = (typeof PX_ACCOUNT_TYPES)[keyof typeof PX_ACCOUNT_TYPES];

export const isBankPxAccountType = (accountType?: string): boolean =>
	accountType === PX_ACCOUNT_TYPES.BANK_DDA ||
	accountType === PX_ACCOUNT_TYPES.STASH_BANK;

//Used for analytics events
export const ANALYTICS_PX_ACCOUNT_TYPE: Partial<Record<PxAccountType, ProductEventsAccountType>> = {
	[PX_ACCOUNT_TYPES.PERSONAL_BROKERAGE]: AnalyticsAccountType.personalBrokerage,
	[PX_ACCOUNT_TYPES.ROBO_PERSONAL_BROKERAGE]: AnalyticsAccountType.personalBrokerage,
	[PX_ACCOUNT_TYPES.TRADITIONAL_IRA]: AnalyticsAccountType.traditionalIra,
	[PX_ACCOUNT_TYPES.ROTH_IRA]: AnalyticsAccountType.rothIra,
	[PX_ACCOUNT_TYPES.CUSTODIAN]: AnalyticsAccountType.custodial,
	[PX_ACCOUNT_TYPES.BANK_DDA]: AnalyticsAccountType.bank,
};

// Special account type formats used for platform tier product types
export const CUSTODIAL_PRODUCT_TYPE = 'CUSTODIAL';

export const ALL_BROKERAGE_ACCOUNTS = [
	PERSONAL_BROKERAGE,
	ROBO_PERSONAL_BROKERAGE,
	CUSTODIAN,
] as const;

export const ALLOWS_MULTIPLE_ACCOUNTS = {
	CUSTODIAN: true,
} as const;

export const accountTypes = {
	PERSONAL_BROKERAGE: {
		name: 'Personal Brokerage',
	},
	ROBO_PERSONAL_BROKERAGE: {
		name: 'Smart',
	},
	TRADITIONAL_IRA: {
		name: 'Traditional IRA',
	},
	ROTH_IRA: {
		name: 'Roth IRA',
	},
	CUSTODIAN: {
		name: 'Custodial',
	},
	DEBIT: {
		name: 'Debit',
	},
} as const;

export const READABLE_ACCOUNT_NAMES = {
	PERSONAL_BROKERAGE: 'Personal Portfolio',
	ROBO_PERSONAL_BROKERAGE: 'Smart Portfolio',
	TRADITIONAL_IRA: 'Traditional IRA',
	ROTH_IRA: 'Roth IRA',
	CUSTODIAN: 'Custodial',
} as const;

// https://github.com/StashInvest/stash-invest-rails/blob/edge/app/services/platform_tiers/constants.rb
export const MONOLITH_PRODUCT_TYPES = {
	PERSONAL_BROKERAGE: 'PERSONAL_BROKERAGE',
	ROBO_PERSONAL_BROKERAGE: 'ROBO_PERSONAL_BROKERAGE',
	IRA: 'IRA',
	CUSTODIAL: 'CUSTODIAL',
	STASH_BANK: 'STASH_BANK',
	PREMIUM_STOCK_REWARD: 'PREMIUM_STOCK_REWARD',
	LEARN_INSIGHT: 'LEARN_INSIGHT',
	PREMIUM_EXCLUSIVE_CONTENT: 'PREMIUM_EXCLUSIVE_CONTENT',
	GROWTH_EXCLUSIVE_CONTENT: 'GROWTH_EXCLUSIVE_CONTENT',
	BEGINNER_EXCLUSIVE_CONTENT: 'BEGINNER_EXCLUSIVE_CONTENT',
	METAL_CARD_STANDARD: 'Standard',
	METAL_CARD_PREMIUM: 'Premium',
	STOCK_BACK_DOUBLE: 'DoubleStockBack',
	STOCK_BACK_DEFAULT: 'DefaultStockBack',
} as const;

export type MonolithProductType =
	(typeof MONOLITH_PRODUCT_TYPES)[keyof typeof MONOLITH_PRODUCT_TYPES];
